/**
 * Exam lobby (brief §exam: `/exam`). Real-exam rules and allowed-aids
 * pre-flight, duration presets, per-slot "N osedda av M" table, the
 * "Starta en övningstenta" card, "Skriv {term}" / "Skriv {paper}" buttons,
 * resume button, history table.
 *
 * The practice papers (PE-01 …) run the identical session lifecycle as the
 * archived exams — same timer, same submit, same self-grading — but are
 * newly written training material, not official papers, and the lobby says so.
 * They are served verified-only, so the block is hidden while the import is
 * still in draft.
 *
 * Deep links. The archive (`/library/exams`) links here with `?past=HT24` or
 * `?paper=PE-03` (`content/links.ts`). The lobby never starts a paper on its
 * own — a timed session that begins without a click would be a trap — so the
 * query parameter only scrolls the matching button into view, focuses it and
 * rings it, and says in one line that the clock starts on click.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import {
  Badge,
  Button,
  Card,
  Modal,
  PageHeader,
  Spinner,
  Table,
  useToast,
} from "@client/components/ui";
import { useLang, useT, type TFunction } from "@client/i18n";
import { EXAM_TOTAL_POINTS, THRESHOLD_G, THRESHOLD_VG } from "@shared/exam";
import { formatDate, formatNumber, formatPoints } from "@client/lib/format";
import { messageForError } from "@client/lib/errors";
import { useExamLobby, useStartExam } from "./api";
import { paperLabel, paperLabelOrGenerated } from "./paper";
import type { HistoryEntryDto, PastExamTerm } from "./types";
import { LastPlanNote } from "./wrapper/LastPlanNote";

const DURATION_PRESETS: { minutes: number; labelKey: string; plainLabelKey: string }[] = [
  {
    minutes: 300,
    labelKey: "exam.lobby.duration.5h",
    plainLabelKey: "exam.lobby.durationPlain.5h",
  },
  {
    minutes: 180,
    labelKey: "exam.lobby.duration.3h",
    plainLabelKey: "exam.lobby.durationPlain.3h",
  },
  {
    minutes: 90,
    labelKey: "exam.lobby.duration.1h30",
    plainLabelKey: "exam.lobby.durationPlain.1h30",
  },
];

const AID_KEYS = ["handbook", "tables", "calculator", "formulaSheet"] as const;

function statusTone(
  status: HistoryEntryDto["status"],
): "neutral" | "accent" | "success" | "warning" {
  if (status === "graded") return "success";
  if (status === "submitted") return "accent";
  if (status === "expired") return "warning";
  return "neutral"; // in_progress, abandoned
}

const HISTORY_LINK =
  "text-sm font-medium text-accent-700 underline hover:text-accent-800 dark:text-accent-300 dark:hover:text-accent-200";

/**
 * Where a history row leads: a graded paper to its result, a handed-in one to
 * grading. An abandoned one leads nowhere: it has no result and no grade page.
 */
function historyAction(row: HistoryEntryDto, t: TFunction) {
  if (row.status === "graded") {
    return (
      <Link to={`/exam/${row.id}/result`} className={HISTORY_LINK}>
        {t("exam.lobby.history.open")}
      </Link>
    );
  }
  if (row.status === "submitted" || row.status === "expired") {
    return (
      <Link to={`/exam/${row.id}/grade`} className={HISTORY_LINK}>
        {t("exam.lobby.history.grade")}
      </Link>
    );
  }
  // abandoned (no result, no grade page) and in_progress (the resume card): no link.
  return null;
}

/** The ring the deep-linked button wears until the student clicks it. */
const HIGHLIGHT_CLASS =
  "ring-2 ring-accent-600 ring-offset-2 ring-offset-white dark:ring-accent-400 dark:ring-offset-ink-950";

/** What `?past=` / `?paper=` resolved to against the papers the server actually has. */
type Requested =
  | { state: "none" }
  | { state: "unknown"; label: string }
  | { state: "busy"; label: string }
  | { state: "ready"; label: string; pastExam?: PastExamTerm; practicePaper?: string };

export function ExamLobbyPage() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const toast = useToast();
  const lobbyQuery = useExamLobby();
  const startMutation = useStartExam();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const historyHeadingId = useId();

  // Arriving from "Avbryt övningstentan": the page the student was on is gone,
  // so focus would otherwise fall to <body>. `#main` is the shell's own script
  // focus target (`tabIndex={-1}`).
  const arrivedFromAbandon = Boolean((location.state as { abandoned?: boolean } | null)?.abandoned);
  useEffect(() => {
    if (arrivedFromAbandon) document.getElementById("main")?.focus();
  }, [arrivedFromAbandon]);

  const askedPast = searchParams.get("past");
  const askedPaper = searchParams.get("paper");
  const lobby = lobbyQuery.data;

  const requested = useMemo<Requested>(() => {
    const label = askedPast ?? askedPaper;
    if (!label || !lobby) return { state: "none" };
    const pastExam = lobby.pastExams.find((term) => term === askedPast);
    const paper = lobby.practicePapers.find((entry) => entry.paper === askedPaper);
    if (!pastExam && !paper) return { state: "unknown", label };
    if (lobby.active) return { state: "busy", label };
    return pastExam
      ? { state: "ready", label: pastExam, pastExam }
      : { state: "ready", label: paper!.paper, practicePaper: paper!.paper };
  }, [askedPast, askedPaper, lobby]);

  const highlightRef = useRef<HTMLButtonElement | null>(null);
  const readyLabel = requested.state === "ready" ? requested.label : null;
  useEffect(() => {
    if (!readyLabel) return;
    const button = highlightRef.current;
    if (!button) return;
    button.scrollIntoView?.({ block: "center", behavior: "smooth" });
    button.focus({ preventScroll: true });
  }, [readyLabel]);

  async function start(body: {
    durationMinutes?: number;
    pastExam?: PastExamTerm;
    practicePaper?: string;
  }) {
    try {
      await startMutation.mutateAsync(body);
      navigate("/exam/session");
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  /**
   * UX-06: one tap used to start a timed session immediately — a mis-tap on
   * "5 timmar" on a phone began a real 5-hour clock with no way back short
   * of handing in an empty paper. Every start path now asks first, defaults
   * to "Avbryt" (the footer order, same pattern as the submit-confirm
   * modal), and names the duration and that the clock cannot be paused.
   */
  const [pendingStart, setPendingStart] = useState<{
    args: { durationMinutes?: number; pastExam?: PastExamTerm; practicePaper?: string };
    /** A plain duration phrase for the confirm sentence — never the button's
     * own label, which can carry a parenthetical ("5 timmar (som riktig
     * tenta)") that reads oddly interpolated mid-sentence. */
    plainDurationLabel: string;
    /** "Tenta HT24" / "Övningstenta PE-03" — named in the dialog on the past-exam
     * and practice-paper paths (review-A minor); absent for a generated exam. */
    paperName?: string;
  } | null>(null);

  async function confirmStart() {
    if (!pendingStart) return;
    const { args } = pendingStart;
    setPendingStart(null);
    await start(args);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title={t("exam.nav")} description={t("exam.lobby.description")} />

      <Modal
        open={!!pendingStart}
        onClose={() => setPendingStart(null)}
        title={t("exam.lobby.startConfirm.title")}
        description={
          <>
            {pendingStart?.paperName && (
              <>
                {pendingStart.paperName}
                <br />
              </>
            )}
            {t("exam.lobby.startConfirm.body", {
              duration: pendingStart?.plainDurationLabel ?? "",
            })}
          </>
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingStart(null)}>
              {t("common.cancel")}
            </Button>
            {/* No `loading` here (review-A minor): `confirmStart` closes this
                modal *before* the mutation starts, so this button is already
                unmounted by the time `startMutation.isPending` would ever be
                true — the underlying lobby buttons carry that state instead. */}
            <Button onClick={() => void confirmStart()}>
              {t("exam.lobby.startConfirm.confirm")}
            </Button>
          </>
        }
      />

      {(requested.state === "unknown" || requested.state === "busy") && (
        <p
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-100 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100"
        >
          {t(`exam.lobby.requested.${requested.state}`, { paper: requested.label })}
        </p>
      )}

      <Card title={t("exam.lobby.aids.title")}>
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-700 dark:text-ink-200">
          {AID_KEYS.map((key) => (
            <li key={key}>{t(`exam.lobby.aids.${key}`)}</li>
          ))}
        </ul>
      </Card>

      {lobbyQuery.isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" label={t("common.loading")} />
        </div>
      )}

      {lobbyQuery.data && (
        <>
          {lobbyQuery.data.active ? (
            <Card title={t("exam.lobby.active.title")}>
              <p className="text-sm text-ink-600 dark:text-ink-400">
                {t("exam.lobby.active.body")}
              </p>
              <Button className="mt-4" onClick={() => navigate("/exam/session")}>
                {t("exam.lobby.resume")}
              </Button>
            </Card>
          ) : (
            <Card title={t("exam.lobby.start.title")}>
              {lobbyQuery.data.lastPlan && (
                <LastPlanNote plan={lobbyQuery.data.lastPlan} className="mb-4" />
              )}
              {requested.state === "ready" && (
                <p
                  role="status"
                  className="mb-3 rounded-xl border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900 dark:border-accent-700 dark:bg-accent-950 dark:text-accent-100"
                >
                  {t("exam.lobby.requested.ready", { paper: requested.label })}
                </p>
              )}
              <p className="mb-3 text-sm text-ink-600 dark:text-ink-400">
                {t("exam.lobby.start.body")}
              </p>
              <p className="mb-3 text-sm text-ink-600 dark:text-ink-400">
                {t("exam.lobby.start.thresholds", {
                  max: formatNumber(EXAM_TOTAL_POINTS, lang),
                  g: formatNumber(THRESHOLD_G, lang),
                  vg: formatNumber(THRESHOLD_VG, lang),
                })}
              </p>
              {lobbyQuery.data.generatedAvailable === false ? (
                <p
                  role="status"
                  className="rounded-xl border border-ink-200 bg-ink-50 px-4 py-3 text-sm text-ink-800 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100"
                >
                  {t("exam.lobby.start.unreleased")}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {DURATION_PRESETS.map((preset) => (
                    <Button
                      key={preset.minutes}
                      variant="secondary"
                      loading={startMutation.isPending}
                      onClick={() =>
                        setPendingStart({
                          args: { durationMinutes: preset.minutes },
                          plainDurationLabel: t(preset.plainLabelKey),
                        })
                      }
                    >
                      {t(preset.labelKey)}
                    </Button>
                  ))}
                </div>
              )}

              <Table
                className="mt-6"
                caption={t("exam.lobby.pools.title")}
                rowKey={(row) => String(row.slot)}
                columns={[
                  { key: "slot", header: t("exam.lobby.pools.problem"), cell: (row) => row.slot },
                  {
                    key: "points",
                    header: t("exam.lobby.pools.points"),
                    cell: (row) => formatPoints(row.points, lang),
                  },
                  {
                    key: "chapters",
                    header: t("exam.lobby.pools.chapters"),
                    cell: (row) => row.chapters.join(", "),
                  },
                  {
                    key: "status",
                    header: t("exam.lobby.pools.status"),
                    cell: (row) =>
                      t("exam.lobby.pools.unseenOfEligible", {
                        unseen: row.unseen,
                        eligible: row.eligible,
                      }),
                  },
                ]}
                rows={lobbyQuery.data.pools}
              />

              <div className="mt-6">
                <p className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
                  {t("exam.lobby.pastExams.title")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {lobbyQuery.data.pastExams.map((exam) => {
                    const highlighted = requested.state === "ready" && requested.pastExam === exam;
                    return (
                      <Button
                        key={exam}
                        ref={highlighted ? highlightRef : undefined}
                        variant={highlighted ? "secondary" : "ghost"}
                        className={highlighted ? HIGHLIGHT_CLASS : undefined}
                        loading={startMutation.isPending}
                        onClick={() =>
                          setPendingStart({
                            args: { pastExam: exam },
                            plainDurationLabel: t("exam.lobby.durationPlain.5h"),
                            paperName: t("exam.lobby.startConfirm.pastExam", { term: exam }),
                          })
                        }
                      >
                        {t("exam.lobby.pastExams.start", { exam })}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {lobbyQuery.data.practicePapers.length > 0 && (
                <div className="mt-6">
                  <p className="mb-1 text-sm font-medium text-ink-700 dark:text-ink-200">
                    {t("exam.lobby.practicePapers.title")}
                  </p>
                  <p className="mb-2 text-sm text-ink-600 dark:text-ink-400">
                    {t("exam.lobby.practicePapers.note")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {lobbyQuery.data.practicePapers.map((paper) => {
                      const highlighted =
                        requested.state === "ready" && requested.practicePaper === paper.paper;
                      return (
                        <Button
                          key={paper.paper}
                          ref={highlighted ? highlightRef : undefined}
                          variant={highlighted ? "secondary" : "ghost"}
                          className={highlighted ? HIGHLIGHT_CLASS : undefined}
                          loading={startMutation.isPending}
                          onClick={() =>
                            setPendingStart({
                              args: { practicePaper: paper.paper },
                              plainDurationLabel: t("exam.lobby.durationPlain.5h"),
                              paperName:
                                paperLabel(
                                  {
                                    kind: "practice-paper",
                                    pastExam: null,
                                    practicePaper: paper.paper,
                                  },
                                  t,
                                ) ?? undefined,
                            })
                          }
                        >
                          {t("exam.lobby.practicePapers.start", { paper: paper.paper })}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/*
            UX-05 fix 2: the action link ("Visa resultat"/"Rätta") sits in
            the table's last column, which a narrow phone genuinely has to
            scroll the table itself sideways to reach — confirmed live
            (`toBeInViewport()` failing at 390 px even after the sr-only
            header stopped escaping the scroller). Below `sm`, each row is
            its own stacked card instead, with the action link always on
            screen.
          */}
          {lobbyQuery.data.history.length > 0 && (
            <>
              {/* Desktop names the history with the table caption; below `sm`
                  this heading is the phone list's name (`aria-labelledby`). */}
              <h2
                id={historyHeadingId}
                className="font-display text-lg text-ink-900 dark:text-ink-50 sm:hidden"
              >
                {t("exam.lobby.history.title")}
              </h2>
              <ul className="space-y-2 sm:hidden" aria-labelledby={historyHeadingId}>
                {lobbyQuery.data.history.map((row) => (
                  <li key={row.id} className="surface p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-ink-600 dark:text-ink-400">
                        {formatDate(row.startedAt, lang)}
                      </span>
                      <Badge tone={statusTone(row.status)}>{t(`exam.status.${row.status}`)}</Badge>
                    </div>
                    <div className="mt-1 font-medium text-ink-800 dark:text-ink-100">
                      {paperLabelOrGenerated(row, t)}
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-ink-700 dark:text-ink-200">
                      <span>
                        {row.totalScore === null ? "—" : formatPoints(row.totalScore, lang)}
                        {row.verdict ? ` · ${t(`exam.verdict.${row.verdict}`)}` : ""}
                      </span>
                      {historyAction(row, t)}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          <Table
            className="hidden sm:block"
            caption={t("exam.lobby.history.title")}
            rowKey={(row) => row.id}
            empty={
              <p className="text-sm text-ink-500 dark:text-ink-400">
                {t("exam.lobby.history.empty")}
              </p>
            }
            columns={[
              {
                key: "date",
                header: t("exam.lobby.history.date"),
                cell: (row) => formatDate(row.startedAt, lang),
              },
              {
                key: "kind",
                header: t("exam.lobby.history.kind"),
                cell: (row) => paperLabelOrGenerated(row, t),
              },
              {
                key: "status",
                header: t("exam.lobby.history.status"),
                cell: (row) => (
                  <Badge tone={statusTone(row.status)}>{t(`exam.status.${row.status}`)}</Badge>
                ),
              },
              {
                key: "score",
                header: t("exam.lobby.history.score"),
                cell: (row) => (row.totalScore === null ? "—" : formatPoints(row.totalScore, lang)),
              },
              {
                key: "verdict",
                header: t("exam.lobby.history.verdict"),
                cell: (row) => (row.verdict ? t(`exam.verdict.${row.verdict}`) : "—"),
              },
              {
                key: "action",
                header: <span className="sr-only">{t("exam.lobby.history.actions")}</span>,
                cell: (row) => historyAction(row, t),
              },
            ]}
            rows={lobbyQuery.data.history}
          />
        </>
      )}
    </div>
  );
}

export default ExamLobbyPage;
